/**
 * Junne theme runtime — base class + pub/sub bus.
 *
 * Imported by every other custom-element module; declares <theme-root>
 * (no-op element) so theme.js loads via <script type="module"> on every page.
 *
 * Custom-element modules import the base via:
 *   import { ThemeElement, ThemeBus, money } from "{{ 'theme.js' | asset_url }}";
 * but ES module imports inside Liquid asset URLs aren't trivial — instead we
 * attach to window: globalThis.ThemeElement, globalThis.ThemeBus, globalThis.JunneMoney.
 */

class ThemeElement extends HTMLElement {
  constructor() {
    super();
    this._cleanup = [];
  }
  $(sel, root = this) { return root.querySelector(sel); }
  $$(sel, root = this) { return Array.from(root.querySelectorAll(sel)); }
  on(target, event, handler, options) {
    target.addEventListener(event, handler, options);
    this._cleanup.push(() => target.removeEventListener(event, handler, options));
    return handler;
  }
  emit(event, detail) {
    this.dispatchEvent(new CustomEvent(event, { detail, bubbles: true, composed: true }));
  }
  disconnectedCallback() {
    for (const fn of this._cleanup) fn();
    this._cleanup = [];
  }
}

const ThemeBus = new EventTarget();

/* ---- helpers ---- */

function debounce(fn, delay = 200) {
  let t;
  return function (...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), delay);
  };
}

function throttle(fn, delay = 100) {
  let last = 0, queued = null;
  return function (...args) {
    const now = Date.now();
    if (now - last >= delay) {
      last = now;
      fn.apply(this, args);
    } else {
      clearTimeout(queued);
      queued = setTimeout(() => {
        last = Date.now();
        fn.apply(this, args);
      }, delay - (now - last));
    }
  };
}

async function fetchSection(sectionId, params = {}) {
  const url = new URL(window.location.href);
  url.searchParams.set('section_id', sectionId);
  for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`fetchSection ${sectionId}: ${res.status}`);
  return await res.text();
}

function trapFocus(el) {
  const focusables = el.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );
  if (!focusables.length) return () => {};
  const first = focusables[0], last = focusables[focusables.length - 1];
  function onKey(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
  el.addEventListener('keydown', onKey);
  first.focus();
  return () => el.removeEventListener('keydown', onKey);
}

function lockBodyScroll() {
  document.body.style.overflow = 'hidden';
  document.documentElement.style.scrollbarGutter = 'stable';
}
function unlockBodyScroll() {
  document.body.style.overflow = '';
  document.documentElement.style.scrollbarGutter = '';
}

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

globalThis.ThemeElement = ThemeElement;
globalThis.ThemeBus = ThemeBus;
globalThis.JunneTheme = { debounce, throttle, fetchSection, trapFocus, lockBodyScroll, unlockBodyScroll, reducedMotion };

/* No-op anchor element so this file is registered as a custom-element module. */
class ThemeRoot extends HTMLElement {}
if (!customElements.get('theme-root')) customElements.define('theme-root', ThemeRoot);
